<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useBriefStore } from "@/stores/briefs";
import TaskDrawer from "@/components/TaskDrawer.vue";
import { api } from "@/services/api";

const props = defineProps<{
  showHistory?: boolean;
  briefId?: number;
  startNew?: boolean;
}>();
const emit = defineEmits<{ openBrief: [id: number] }>();
const store = useBriefStore();
const { t, locale } = useI18n();
const input = ref("");
const changeInput = ref("");
const changeOpen = ref(false);
const messagesPane = ref<HTMLElement | null>(null);
const editingTitle = ref(false);
const titleInput = ref("");
const availableSkills = ref<Array<{ id: string; name: string; description: string }>>([]);
const active = computed(() => store.active);
const clientText = (task: {
  client_title?: string;
  title: string;
  client_scope?: string;
  scope: string;
}) => ({
  title: task.client_title || task.title,
  scope: task.client_scope || task.scope,
});
let refreshTimer: number | undefined;
const intro = computed(() =>
  locale.value.startsWith("fr")
    ? "Decrivez votre besoin dans votre propre langue, puis confirmez la liste des taches."
    : locale.value.startsWith("en")
      ? "Describe the requirement in your own language and confirm the task list before submission."
      : "像聊天一样补充需求，确认后再提交给团队。",
);
const inputPlaceholder = computed(() =>
  locale.value.startsWith("fr")
    ? "Ecrivez votre besoin dans votre propre langue…"
    : locale.value.startsWith("en")
      ? "Write your requirement in your own language…"
      : "输入中文、英文、法语或混合语言的需求内容…",
);
const statusText = (status: string) => {
  const labels = locale.value.startsWith("fr")
    ? {
        draft: "A confirmer",
        submitted: "Recu",
        in_progress: "En cours",
        ready_for_acceptance: "Pret a valider",
        accepted: "Accepte",
        revision_requested: "Modification demandee",
      }
    : locale.value.startsWith("en")
      ? {
          draft: "Awaiting confirmation",
          submitted: "Received",
          in_progress: "In progress",
          ready_for_acceptance: "Ready for review",
          accepted: "Accepted",
          revision_requested: "Revision requested",
        }
      : {
          draft: "待确认",
          submitted: "已接收",
          in_progress: "制作中",
          ready_for_acceptance: "待客户验收",
          accepted: "已验收",
          revision_requested: "客户要求修改",
        };
  return labels[status as keyof typeof labels] || status;
};
async function loadActiveBrief() {
  await store.loadList();
  if (props.startNew) await store.create();
  else if (props.briefId) await store.open(props.briefId);
  else if (store.briefs[0]) await store.open(store.briefs[0].id);
  else await store.create();
}
onMounted(async () => {
  availableSkills.value = (await api<{ skills: Array<{ id: string; name: string; description: string }> }>("/skills")).skills;
  await loadActiveBrief();
  refreshTimer = window.setInterval(() => {
    void store.refresh().catch(() => undefined);
  }, 8000);
});
watch(
  () => [props.briefId, props.startNew],
  async () => {
    if (!props.showHistory) await loadActiveBrief();
  },
);
async function scrollMessagesToBottom() {
  await nextTick();
  if (messagesPane.value) messagesPane.value.scrollTop = messagesPane.value.scrollHeight;
}
watch(() => active.value?.messages.length, () => { void scrollMessagesToBottom() })
watch(() => active.value?.id, () => { void scrollMessagesToBottom() })
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
async function send() {
  const content = input.value.trim();
  if (!content) return;
  input.value = "";
  try {
    await store.sendRequirement(content);
  } catch {
    input.value = content;
  }
}
async function addChange() {
  if (!changeInput.value.trim()) return;
  await store.createChange(changeInput.value);
  changeInput.value = "";
  changeOpen.value = false;
}
async function refreshTask() {
  if (!store.activeTask) return;
  await store.openTask(store.activeTask.id);
  await store.refresh();
}
async function openBrief(id: number) {
  await store.open(id);
  emit("openBrief", id);
}
function editTitle() { titleInput.value = active.value?.title || ""; editingTitle.value = true }
async function saveTitle() { if (!titleInput.value.trim()) return; await store.renameActive(titleInput.value.trim()); editingTitle.value = false }
async function changeSkill(skillId: string) { if (!active.value) return; store.active = (await api<{ brief: any }>(`/briefs/${active.value.id}/skill`, { method: "PATCH", body: JSON.stringify({ skillId }) })).brief; await store.loadList() }
function assistantText(content: string) {
  try {
    const parsed = JSON.parse(content);
    return locale.value.startsWith("zh")
      ? parsed.reply_client || parsed.summary_zh
      : parsed.reply_client || parsed.summary_client || parsed.summary_zh;
  } catch {
    return content;
  }
}
function assistantQuestions(content: string): string[] {
  try {
    const questions = JSON.parse(content).open_questions;
    return Array.isArray(questions) ? questions : [];
  } catch {
    return [];
  }
}
function formatMessage(content: string) {
  const escape = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  return escape(assistantText(content))
    .replace(/^#{1,3}\s+(.+)$/gm, "<strong class=\"agent-heading\">$1</strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/\n/g, "<br>")
}
</script>

<template>
  <div v-if="showHistory" class="history-page">
    <header class="page-head">
      <div>
        <h1>{{ t("myBriefs") }}</h1>
        <p class="muted">
          {{
            locale.startsWith("zh")
              ? "每一份 Brief 都保留独立会话、任务、变更和交付记录。"
              : "Every Brief keeps its own conversation, tasks, changes, and deliveries."
          }}
        </p>
      </div>
      <button class="btn btn--primary" @click="store.create()">
        {{ t("newBrief") }}
      </button>
    </header>
    <div class="history-list">
      <button
        v-for="brief in store.briefs"
        :key="brief.id"
        class="history-card"
        @click="openBrief(brief.id)"
      >
        <span
          ><b>{{ brief.title }}</b
          ><small>{{ statusText(brief.status) }}</small></span
        ><span class="muted">{{
          brief.updated_at.slice(0, 16).replace("T", " ")
        }}</span>
      </button>
      <p v-if="!store.briefs.length" class="muted">
        {{ locale.startsWith("zh") ? "还没有 Brief。" : "No Briefs yet." }}
      </p>
    </div>
  </div>
  <div v-else-if="active" class="client-page">
    <header class="page-head">
      <div>
        <div v-if="editingTitle" class="title-edit"><input v-model="titleInput" class="input" maxlength="120" @keyup.enter="saveTitle" /><button class="btn btn--primary" @click="saveTitle">保存</button><button class="btn" @click="editingTitle = false">取消</button></div>
        <h1 v-else>{{ active.title }} <button class="title-edit__button" title="修改 Brief 标题" @click="editTitle">编辑</button></h1>
        <p class="muted">{{ intro }}</p>
        <select v-if="active.status === 'draft' && availableSkills.length" class="skill-picker" :value="active.skill_id" @change="changeSkill(($event.target as HTMLSelectElement).value)"><option value="brief-confirmation" disabled>选择业务类型</option><option v-for="skill in availableSkills" :key="skill.id" :value="skill.id">{{ skill.name }}</option></select>
      </div>
      <button class="btn btn--primary" @click="store.create()">
        {{ t("newBrief") }}
      </button>
    </header>
    <div class="client-layout">
      <section class="conversation">
        <div ref="messagesPane" class="messages">
          <article
            v-for="message in active.messages"
            :key="message.id"
            class="bubble"
            :class="{ mine: message.role === 'user' }"
          >
            <small>{{
              message.role === "assistant" ? "Brief Agent" : "You"
            }}</small
            ><template v-if="message.role === 'assistant'"
              ><div class="agent-copy" v-html="formatMessage(message.content)"></div>
              <ul
                v-if="assistantQuestions(message.content).length"
                class="questions"
              >
                <li
                  v-for="question in assistantQuestions(message.content)"
                  :key="question"
                >
                  {{ question }}
                </li>
              </ul></template
            ><template v-else>{{ message.content }}</template>
          </article>
        </div>
        <div class="composer">
          <textarea
            v-model="input"
            class="textarea"
            :placeholder="inputPlaceholder"
          /><button
            class="btn btn--primary"
            :disabled="store.loading"
            @click="send"
          >
            {{ t("send") }}
          </button>
        </div>
      </section>
      <aside class="task-panel">
        <h2>{{ t("taskList") }}</h2>
        <div class="summary">
          <span>{{ statusText(active.status) }}</span
          ><b
            >{{ active.tasks.filter((task) => task.confirmed).length }} /
            {{ active.tasks.length }}</b
          >
        </div>
        <article v-for="task in active.tasks" :key="task.id" class="task-card">
          <label
            ><input
              type="checkbox"
              :checked="Boolean(task.confirmed)"
              :disabled="active.status !== 'draft'"
              @change="
                store.confirmTask(
                  task.id,
                  ($event.target as HTMLInputElement).checked,
                )
              "
            />
            <div>
              <b>{{ clientText(task).title }}</b>
              <p>{{ clientText(task).scope }}</p>
              <span
                v-for="item in task.client_deliverables?.length
                  ? task.client_deliverables
                  : task.deliverables"
                :key="item"
                class="chip"
                >{{ item }}</span
              >
            </div></label
          >
          <div class="task-card__foot">
            <span class="status">{{ statusText(task.status) }}</span
            ><button
              v-if="task.confirmed && active.status !== 'draft'"
              class="btn"
              @click="store.openTask(task.id)"
            >
              {{ locale.startsWith("zh") ? "沟通与交付" : "Messages" }}
            </button>
          </div>
        </article>
        <button
          v-if="active.status === 'draft' && active.tasks.length"
          class="btn btn--primary full"
          @click="store.submit"
        >
          {{ t("submit") }}</button
        ><button
          v-else-if="active.status !== 'draft'"
          class="btn full"
          @click="changeOpen = true"
        >
          {{ t("addChange") }}
        </button>
      </aside>
    </div>
    <Teleport to="body"
      ><div v-if="changeOpen" class="modal">
        <form class="modal__panel" @submit.prevent="addChange">
          <h2>{{ t("addChange") }}</h2>
          <p class="muted">
            {{
              locale.startsWith("zh")
                ? "团队将收到 AI 整理后的中文执行说明。"
                : locale.startsWith("fr")
                  ? "L equipe recevra un resume d execution structure en chinois."
                  : "The team receives a structured Chinese execution summary."
            }}
          </p>
          <textarea v-model="changeInput" class="textarea" required />
          <div class="modal__actions">
            <button type="button" class="btn" @click="changeOpen = false">
              取消</button
            ><button class="btn btn--primary">提交</button>
          </div>
        </form>
      </div></Teleport
    ><TaskDrawer
      :task="store.activeTask"
      @close="store.activeTask = null"
      @changed="refreshTask"
    />
  </div>
</template>

<style scoped>
.skill-picker{margin-top:9px;border:1px solid var(--line);padding:6px 8px;background:#fff}
.title-edit{display:flex;align-items:center;gap:7px}.title-edit .input{min-width:260px}.title-edit__button{margin-left:8px;border:0;background:transparent;color:var(--brand-dark);font-size:12px;font-weight:400}.agent-copy{line-height:1.72;word-break:break-word}.agent-copy :deep(.agent-heading){display:block;margin:10px 0 4px;color:var(--ink);font-size:15px}.agent-copy :deep(code){padding:1px 4px;background:#f3f5f5;color:#48545a}.agent-copy :deep(strong){font-weight:700}
.questions {
  margin: 9px 0 0;
  padding-left: 18px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 26px 30px 18px;
  background: #fff;
  border-bottom: 1px solid var(--line);
}
.page-head h1 {
  margin: 0;
  font-size: 22px;
}
.page-head p {
  margin: 5px 0 0;
}
.client-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
}
.conversation {
  display: flex;
  min-height: calc(100vh - 160px);
  flex-direction: column;
}
.messages {
  flex: 1;
  padding: 26px 30px;
}
.bubble {
  max-width: 760px;
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  background: #fff;
}
.bubble.mine {
  margin-left: auto;
  background: #fff0e7;
  border-color: #f2c7a8;
}
.bubble small {
  display: block;
  margin-bottom: 4px;
  color: var(--muted);
}
.composer {
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  padding: 14px 30px;
  background: #fff;
  border-top: 1px solid var(--line);
}
.task-panel {
  padding: 22px 18px;
  border-left: 1px solid var(--line);
  background: #fbfcfc;
}
.task-panel h2 {
  margin: 0 0 12px;
  font-size: 16px;
}
.summary {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.task-card {
  margin-top: 10px;
  padding: 11px;
  border: 1px solid var(--line);
  background: #fff;
}
.task-card label {
  display: flex;
  gap: 9px;
}
.task-card p {
  margin: 4px 0;
  color: var(--muted);
  font-size: 13px;
}
.task-card__foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 9px;
}
.chip {
  display: inline-block;
  margin: 3px 4px 0 0;
  padding: 2px 5px;
  background: #f4f6f7;
  color: var(--muted);
  font-size: 12px;
}
.full {
  width: 100%;
  margin-top: 13px;
}
.modal {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: grid;
  place-items: center;
  background: rgba(20, 30, 40, 0.35);
  padding: 20px;
}
.modal__panel {
  width: min(500px, 100%);
  display: grid;
  gap: 12px;
  padding: 22px;
  background: #fff;
}
.modal__panel h2 {
  margin: 0;
}
.modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
@media (max-width: 700px) {
  .page-head,
  .messages,
  .composer {
    padding-left: 14px;
    padding-right: 14px;
  }
  .client-layout {
    grid-template-columns: 1fr;
  }
  .task-panel {
    order: -1;
    border-left: 0;
    border-bottom: 1px solid var(--line);
  }
  .conversation {
    min-height: 0;
  }
  .composer {
    position: fixed;
    z-index: 5;
    left: 0;
    right: 0;
    padding: 9px 12px;
  }
  .composer .textarea {
    min-height: 46px;
  }
  .messages {
    padding-bottom: 120px;
  }
}
.history-page {
  min-height: calc(100vh - 64px);
}
.history-list {
  max-width: 960px;
  display: grid;
  gap: 10px;
  padding: 24px 30px;
}
.history-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 15px;
  border: 1px solid var(--line);
  background: #fff;
  text-align: left;
}
.history-card:hover {
  border-color: #efb98e;
  background: #fff9f4;
}
.history-card b,
.history-card small {
  display: block;
}
.history-card small {
  margin-top: 3px;
  color: var(--muted);
}
.client-page {
  display: flex;
  height: calc(100dvh - 64px);
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}
.client-layout {
  flex: 1;
  min-height: 0;
}
.conversation {
  height: 100%;
  min-height: 0;
}
.messages {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.task-panel {
  min-height: 0;
  overflow-y: auto;
}
@media (max-width: 700px) {
  .client-page {
    height: calc(100dvh - 56px);
  }
  .client-layout {
    overflow: hidden;
  }
  .task-panel {
    max-height: 36%;
    overflow-y: auto;
  }
}
</style>
